import { IBaseModel } from './base.model';
import { IClient } from './clients.models';
import { Currency, ModeOfPayment } from './currency';
import { IPartner } from './partners.models';
import { IPolicy } from './policy';
import { IQueryParams } from './queryparams';

export type InvoiceStatus =
  | 'Pending'
  | 'Paid'
  | 'PartiallyPaid'
  | 'Partially Paid'
  | 'FullyPaid'
  | 'Fully Paid'
  | 'Overdue'
  | 'Cancelled';

export enum PaymentPlanStatus {
  Pending = 'Pending',
  Active = 'Active',
  PartiallyPaid = 'PartiallyPaid',
  FullyPaid = 'FullyPaid',
  Cancelled = 'Cancelled',
  Suspended = 'Suspended',
}

export type PaymentPlanFrequency = 'Daily' | 'Weekly' | 'Monthly' | 'Custom';
export type PaymentPlanConfigFrequency = Exclude<
  PaymentPlanFrequency,
  'Custom'
>; // No custom for payment plan configs
export type AmountType = 'Uniform' | 'Custom';
export type PaymentPlanConfigStatus =
  | 'Inactive'
  | 'Active'
  | 'Deleted'
  | 'Archived';

export interface ICustomInstallment {
  dueDate: Date;
  amount: number;
}

export interface IPaymentPlanReport {
  referenceNumber: string;
  clientName: string;
  clientPhone: string;
  clientEmail: string;
  policyNumber: string;
  totalAmount: number;
  paidAmount: number;
  outstandingBalance: number;
  currency: string;
  status: string;
  frequency: string;
  numberOfInstallments: number;
  paidInstallments: number;
  overdueInstallments: number;
  pendingInstallments: number;
  nextDueDate: string | null;
  nextDueAmount: number | null;
  insurerName: string;
  intermediaryName: string;
  partnerName: string;
  createdAt: string;
  startDate: string;
}

export interface IPaymentPlanReportParams extends IQueryParams {
  insurerId: number | null;
  clientId: number | null;
  partnerId: number | null;
  intermediaryId: number | null;
  status: string[] | null;
  currency: Currency[] | null;
  fromDate: string | null;
  toDate: string | null;
  query: string | null;
}

export interface IPaymentPlanQueryParams extends IQueryParams {
  status?: string;
  query?: string | null;
  id?: number | null;
  intermediaryId: number | null;
  insurerId?: number | null;
  partnerId?: number | null;
  Currency?: string | null;
}

export interface IPaymentPlan extends IBaseModel {
  clientId: number;
  startDate: Date;
  numberOfInstallments: number;
  firstInstallmentDate: Date;
  totalAmount: number;
  description: string;
  amountType: 'Uniform' | 'Custom';
  frequency: 'Daily' | 'Weekly' | 'Monthly' | 'Custom';
  currency: string;
  partnerId?: number;
  insurerId: number;
  policyId?: number;
  customInstallments?: ICustomInstallment[];
  referenceNumber?: string;
  status?: PaymentPlanStatus;
  outstandingBalance?: number;
  paidAmount?: number;
  client?: IClient;
  partner?: IPartner;
  policy?: IPolicy;
  approvedBy?: string;
  approvedAt?: Date;
  createdBy?: string;
  installments?: IInstallment[];
}

export interface IPaymentPlanRequest {
  clientId: number;
  startDate: Date;
  numberOfInstallments: number;
  firstInstallmentDate: Date;
  totalAmount: number;
  description: string;
  amountType: 'Uniform' | 'Custom';
  frequency: 'Daily' | 'Weekly' | 'Monthly' | 'Custom';
  currency: string;
  insurerId: number;
  partnerId?: number;
  policyId?: number;
  customInstallments?: ICustomInstallment[];
  referenceNumber?: string;
  status?: PaymentPlanStatus;
  outstandingBalance?: number;
  paidAmount?: number;
  client?: IClient;
  partner?: IPartner;
  approvedBy?: string;
  approvedAt?: Date;
  createdBy?: string;
  invoices?: any[];
}

export interface IMissedInstallmentReport {
  policyNumber: string;
  clientName: string;
  insurer: string;
  product: string;
  policyType: string;
  riskDetails: string;
  premiumCharge: number;
  installmentNo: number;
  effectiveDate: string;
  dueDate: string;
  numberOfMissedInstallments: number;
  amountDue: number;
}

export interface IMissedInstallmentReportParams extends IQueryParams {
  insurerId?: number | null;
  clientId?: number | null;
  partnerId?: number | null;
  intermediaryId?: number | null;
  fromDate?: string | null;
  toDate?: string | null;
  query?: string | null;
}

export interface IPaymentPlanLiquidationRequest {
  paymentPlanId: number;
  amountReceived: number;
  paymentMethod: ModeOfPayment;
  accountNumber?: string;
  narration: string;
}

export interface IInstallmentReceipt {
  installmentId: number;
  installment: IInstallment | null;
  receiptId: number;
  receipt: unknown | null;
  allocatedAmount: number;
  allocatedBy: string;
  id: number;
  createdAt: Date | string;
  updatedAt: Date | string;
  proofOfPaymentUrl?: string;
}

export interface IInstallment {
  number: string;
  paymentPlanId: number;
  dueDate: Date | string;
  amount: number;
  paidAmount: number;
  outstandingAmount: number;
  status: InvoiceStatus;
  currency: string;
  installmentNumber: number;
  remarks?: string | null;
  id: number;
  createdAt: Date | string;
  updatedAt: Date | string;
}

export interface IOutstandingPremiumReport {
  policyNumber: string;
  clientName: string;
  insurer: string;
  product: string;
  policyType: string;
  riskDetails: string;
  policyPeriod: string;
  totalPremium: number;
  installmentNo: number;
  amountPaid: number;
  outstandingBalance: number;
  days0To30: number;
  days31To60: number;
  days60To90: number;
  days90Plus: number;
  policyStatus: string;
}

export interface IOutstandingPremiumReportParams extends IQueryParams {
  insurerId?: number | null;
  clientId?: number | null;
  partnerId?: number | null;
  intermediaryId?: number | null;
  fromDate?: string | null;
  toDate?: string | null;
  query?: string | null;
}
export interface ICreatePaymentPlanConfigRequest {
  name: string;
  description: string | null;
  numberOfInstallments: number;
  frequency: PaymentPlanConfigFrequency;
  amountType: 'Uniform';
  insurerId: number;
  requireInitialPayment: boolean;
  status?: PaymentPlanConfigStatus;
  isLevyRequired: boolean;
}

export interface PaymentPlanConfigResponse {
  success: boolean;
  config: IPaymentPlanConfigResponse | null;
}

export interface IPaymentPlanConfigResponse extends IBaseModel {
  name: string;
  description: string;
  numberOfInstallments: number;
  frequency: PaymentPlanConfigFrequency;
  amountType: AmountType;
  insurerId: number;
  status: PaymentPlanConfigStatus;
  requireInitialPayment: boolean;
  isLevyRequired: boolean;
}

export interface IPaymentPlanConfigByIdResponse
  extends IPaymentPlanConfigResponse {}

export interface IPaymentPlanConfigProduct {
  productId: number;
  productName: string;
}

export interface IPaymentPlanConfigIntermediary {
  intermediaryId: number;
  intermediaryName: string;
}

export interface IPaymentPlanConfigQueryParams extends IQueryParams {
  insurerId: number | null;
  productId: number | null;
  status: PaymentPlanConfigStatus | null;
  query: string | null;
}

export interface IAvailablePaymentPlanConfigsRequest {
  insurerId: number | null;
  productId: number | null;
  intermediaryId: number | null;
  partnerId: number | null;
  query?: string | null;
}

export interface ICreatePaymentPlanConfigFromExistingRequest {
  existingConfigId: number;
  partnerId: number | null;
  intermediaryId: number | null;
  productId: number;
  numberOfInstallments: number;
  frequency: PaymentPlanFrequency;
}

export interface IPaymentPlanConfigPartner {
  partnerId: number;
  partnerName: string;
}

export interface IPaymentPlanConfigFromExistingResponse extends IBaseModel {
  name: string;
  description: string;
  frequency: PaymentPlanConfigFrequency;
  numberOfInstallments: number;
  amountType: AmountType;
  insurerId: number;
  status: PaymentPlanConfigStatus;
  isLevyRequired: boolean;
  requireInitialPayment: boolean;
  products: IPaymentPlanConfigProduct[];
  intermediaries: IPaymentPlanConfigIntermediary[];
  partners: IPaymentPlanConfigPartner[];
}

export interface IPaymentPlanConfigPreviewRequest {
  paymentPlanConfigId: number;
  policyId: number;
  customSecondInstallmentDate?: Date | string | null;
  totalAmount?: number | null;
  currency?: string;
  startDate?: Date | null;
  endDate?: Date | null;
  insurerId?: number | null;
  numberOfInstallments?: number | null;
  frequency?: PaymentPlanConfigFrequency | null;
}

export interface PaymentPlanConfigInstallment {
  installmentNumber: number;
  dueDate: Date | string | null;
  amount: number;
  levyAmount: number;
  totalAmount: number;
}

export interface CustomInstallment {
  dueDate: Date | string | null;
  amount: number;
}

export interface IPaymentPlanConfigPreviewResponse {
  paymentPlanConfigId: number;
  policyId: number;
  description: string;
  startDate: string;
  totalAmount: number;
  firstInstallmentDate: string;
  numberOfInstallments: number;
  frequency: string;
  amountType: string;
  currency: string;
  clientId: number;
  partnerId: number | null;
  insurerId: number;
  intermediaryId: number;
  customInstallments: CustomInstallment[];
  isLevyRequired: boolean;
  levyAmount: number;
  installments: PaymentPlanConfigInstallment[];
  initialInstallmentRequired: boolean;
}

export interface IPaymentPlanReminderConfigRequest {
  maxReminders: number;
  reminderIntervalDays: number;
  maxReminderWindowDays: number;
  initialDelayDays: number;
  preDueDaysBeforeDueDate: number;
  preDueMaxReminders: number;
  preDueReminderIntervalDays: number;
  sendWindowStartHour: number;
  sendWindowEndHour: number;
  isActive: boolean;
}

export interface IPaymentPlanReminderConfigResponse extends IBaseModel {
  insurerId: number;
  maxReminders: number;
  reminderIntervalDays: number;
  maxReminderWindowDays: number;
  initialDelayDays: number;
  preDueDaysBeforeDueDate: number;
  preDueMaxReminders: number;
  preDueReminderIntervalDays: number;
  sendWindowStartHour: number;
  sendWindowEndHour: number;
  isActive: boolean;
}
